import React from 'react';
import { iconMap, type IconName } from './index';
import type { IconProps } from './types';

export interface IconComponentProps extends IconProps {
  /**
   * Name of the icon to render
   */
  name: IconName;
}

/**
 * Icon Component
 *
 * A convenient wrapper for rendering icons by name.
 * All icons are extracted from Figma Crop Circle System.
 *
 * @example
 * ```tsx
 * import { Icon } from '@/shared-ui/components/icons';
 *
 * <Icon name="Chevron Right" size={24} color="#FD5000" />
 * ```
 */
export const Icon: React.FC<IconComponentProps> = ({ name, ...props }) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  const { style, ...rest } = props;

  return (
    <IconComponent
      // Prevent stroke clipping for icons that touch the viewBox edges (common with 1.5px strokes)
      overflow="visible"
      // Align joins/caps with typical design-system icon styling; individual icons can override explicitly
      strokeLinecap="round"
      strokeLinejoin="round"
      // Ensure web/RNW respects overflow even if the prop isn't forwarded consistently
      style={[{ overflow: 'visible' } as const, style] as unknown}
      {...rest}
    />
  );
};

Icon.displayName = 'Icon';
